/**
 * Immobili: elenco a sinistra, scheda larga a destra.
 *
 * La scheda non è più un pannellino che sale da sotto: è metà schermo, e il
 * calendario ci sta dentro per intero — che è l'unico modo di guardare un mese
 * e capirne la forma.
 */

import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Building2,
  CalendarCheck,
  CalendarDays,
  Check,
  FileText,
  Euro,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
  Wallet,
  Wand2,
} from "lucide-react";
import {
  addDays,
  formatEur,
  formatRange,
  meseInCorso,
  nightsInRange,
  nomeMese,
  notteBloccata,
  todayInRome,
  type CalendarDayView,
  type IsoDate,
  type ListingView,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { useStore } from "../lib/store.tsx";
import {
  Avatar,
  Badge,
  Card,
  EmptyState,
  IconButton,
  Pill,
  Skeleton,
  Thumb,
  cx,
} from "../ui/primitives.tsx";
import { DetailPanel, ListPanel, ListRow } from "../shell/ListPanel.tsx";
import { TRANSIZIONE_COLONNA } from "../shell/Nav.tsx";
import { AnnuncioCard } from "./AnnuncioCard.tsx";
import { ContiTab } from "./ContiTab.tsx";
import {
  GrigliaKpi,
  IntestazioneRiquadro,
  Kpi,
  Sezione,
} from "../ui/Sezione.tsx";
import { MonthCalendar } from "../ui/MonthCalendar.tsx";
import { Modal } from "../ui/Modal.tsx";
import { daQuandoLetto } from "../lib/quando.ts";

export function Listings({
  onAskOtto,
  focusId,
}: {
  onAskOtto: (prompt: string) => void;
  /** Immobile da aprire arrivando da un'altra sezione (per esempio un consiglio). */
  focusId?: string | null;
}) {
  const [listings, setListings] = useState<ListingView[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [opened, setOpened] = useState(false);
  // L'elenco si può ridurre a una colonna di sole foto: la scheda a destra
  // prende il posto, e con tre case le foto bastano a riconoscerle.
  const [compatta, setCompatta] = useState(() => {
    try {
      return localStorage.getItem("otto.immobili.compatta") === "1";
    } catch {
      return false;
    }
  });
  const riduci = (valore: boolean) => {
    setCompatta(valore);
    try {
      localStorage.setItem("otto.immobili.compatta", valore ? "1" : "0");
    } catch {
      // Senza storage si perde solo la preferenza fra una sessione e l'altra.
    }
  };

  useEffect(() => {
    void api
      .listings()
      .then(({ listings: loaded }) => {
        setListings(loaded);
        setSelectedId((current) => current ?? focusId ?? loaded[0]?.id ?? null);
      })
      .catch(() => setListings([]));
    // `focusId` è letto solo al primo caricamento: l'effetto sotto si occupa
    // dei cambi successivi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!focusId) return;
    setSelectedId(focusId);
    setOpened(true);
  }, [focusId]);

  const filtered = useMemo(() => {
    if (!listings) return [];
    const q = query.trim().toLowerCase();
    if (!q) return listings;
    return listings.filter((l) =>
      `${l.nickname} ${l.city ?? ""} ${l.title ?? ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [listings, query]);

  const selected = listings?.find((l) => l.id === selectedId) ?? null;

  return (
    <div className="flex h-full">
      {/* Una colonna sola che si stringe: l'elenco completo sfuma e scivola
          via, la striscia di foto compare al suo posto. Larghezza animata,
          il resto la segue. */}
      <div
        className={cx(
          "relative h-full shrink-0 overflow-hidden border-line bg-surface md:border-r",
          TRANSIZIONE_COLONNA,
          opened ? "hidden md:block" : "block",
          compatta ? "w-[88px]" : "w-full md:w-[352px]",
        )}
      >
        <div
          className={cx(
            "h-full w-full transition-[opacity,transform] duration-calm ease-soft md:w-[352px]",
            compatta && "pointer-events-none -translate-x-3 opacity-0",
          )}
          aria-hidden={compatta}
        >
          <ListPanel
            collapsed={opened}
            action={
              // Su telefono l'elenco non si riduce: occupa già tutto lo
              // schermo, e la scheda lo copre quando si apre.
              <span className="hidden md:block">
                <IconButton
                  label="Riduci l'elenco"
                  onClick={() => riduci(true)}
                >
                  <PanelLeftClose size={16} />
                </IconButton>
              </span>
            }
            query={query}
            onQuery={setQuery}
            searchPlaceholder="Cerca per nome o città…"
          >
            {!listings ? (
              <div className="flex flex-col gap-2 px-1">
                {Array.from({ length: 7 }, (_, index) => (
                  <Skeleton key={index} className="h-[68px]" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={<Building2 size={24} />}
                title="Nessun immobile trovato"
              />
            ) : (
              groupByCity(filtered).map(([city, group]) => (
                <div key={city} className="mb-2">
                  <p className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                    {city} · {group.length}
                  </p>
                  {group.map((listing) => (
                    <ListRow
                      key={listing.id}
                      active={listing.id === selectedId}
                      onClick={() => {
                        setSelectedId(listing.id);
                        setOpened(true);
                      }}
                      media={
                        <Thumb
                          src={listing.photoUrl}
                          alt={listing.nickname}
                          size={44}
                        />
                      }
                      title={listing.nickname}
                      meta={
                        listing.stats?.avgPriceCents
                          ? formatEur(listing.stats.avgPriceCents)
                          : undefined
                      }
                      preview={
                        listing.stats &&
                        listing.stats.occupancyPercent != null ? (
                          <span className="flex items-center gap-2">
                            <span className="h-1.5 w-14 overflow-hidden rounded-pill bg-surface-sunken">
                              <span
                                className={cx(
                                  "block h-full rounded-pill transition-all duration-calm",
                                  listing.stats.occupancyPercent >= 70
                                    ? "bg-ok"
                                    : listing.stats.occupancyPercent >= 35
                                      ? "bg-accent"
                                      : "bg-warn",
                                )}
                                style={{
                                  width: `${listing.stats.occupancyPercent}%`,
                                }}
                              />
                            </span>
                            {listing.stats.occupancyPercent}% occupato
                          </span>
                        ) : (
                          "Nessun dato di calendario"
                        )
                      }
                    />
                  ))}
                </div>
              ))
            )}
          </ListPanel>
        </div>

        <div
          className={cx(
            "absolute inset-y-0 left-0 flex w-[88px] flex-col items-stretch bg-surface-muted pb-3 transition-[opacity,transform] duration-calm ease-soft",
            compatta
              ? "translate-x-0 opacity-100 delay-100"
              : "pointer-events-none -translate-x-3 opacity-0",
          )}
          aria-hidden={!compatta}
        >
          <div className="flex shrink-0 justify-center border-b border-line bg-surface py-2.5">
            <IconButton
              label="Allarga l'elenco"
              tone="sunken"
              onClick={() => riduci(false)}
            >
              <PanelLeftOpen size={16} />
            </IconButton>
          </div>
          <div className="scroll-soft flex min-h-0 flex-1 flex-col items-center gap-2.5 overflow-y-auto px-2 pt-3">
            {(listings ?? []).map((listing) => (
              <button
                key={listing.id}
                type="button"
                tabIndex={compatta ? 0 : -1}
                title={`${listing.nickname}${listing.city ? ` · ${listing.city}` : ""}`}
                onClick={() => {
                  setSelectedId(listing.id);
                  setOpened(true);
                }}
                className={cx(
                  "rounded-card bg-surface p-1 shadow-soft transition-all duration-quick ease-soft",
                  listing.id === selectedId
                    ? "ring-2 ring-accent"
                    : "ring-1 ring-line hover:ring-line-strong",
                )}
              >
                <Thumb
                  src={listing.photoUrl}
                  alt={listing.nickname}
                  size={48}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      <DetailPanel open={opened}>
        <ListingDetail
          listing={selected}
          onAskOtto={onAskOtto}
          onBack={() => setOpened(false)}
        />
      </DetailPanel>
    </div>
  );
}

function groupByCity(listings: ListingView[]): Array<[string, ListingView[]]> {
  const byCity = new Map<string, ListingView[]>();
  for (const listing of listings) {
    const city = listing.city ?? "Senza città";
    byCity.set(city, [...(byCity.get(city) ?? []), listing]);
  }
  return [...byCity.entries()];
}

/**
 * Le tre domande che un property manager fa a un immobile.
 *
 * Tre, non una pila di riquadri tutti aperti: aprendo una casa si vuole sapere
 * *una* cosa per volta — com'è pieno il mese, quanto ha reso, com'è scritto
 * l'annuncio. Metterle tutte a schermo insieme costringe a cercare.
 */
type Scheda = "calendario" | "conti" | "annuncio";

const SCHEDE: Array<{ id: Scheda; label: string; Icon: typeof CalendarDays }> =
  [
    { id: "calendario", label: "Calendario", Icon: CalendarDays },
    { id: "conti", label: "Conti", Icon: Wallet },
    { id: "annuncio", label: "Annuncio", Icon: FileText },
  ];

// ── Scheda ──────────────────────────────────────────────────────────────────

function ListingDetail({
  listing,
  onAskOtto,
  onBack,
}: {
  listing: ListingView | null;
  onAskOtto: (prompt: string) => void;
  onBack: () => void;
}) {
  const store = useStore();
  const [days, setDays] = useState<CalendarDayView[] | null>(null);
  const [floor, setFloor] = useState("");
  const [saving, setSaving] = useState(false);
  const [cin, setCin] = useState("");
  const [savingCin, setSavingCin] = useState(false);
  const [range, setRange] = useState<{ from: IsoDate; to: IsoDate } | null>(
    null,
  );
  /**
   * Fin dove arrivano i giorni che abbiamo in mano.
   *
   * Si carica una finestra sola — un mese indietro e 240 giorni avanti — e
   * oltre quel bordo il calendario disegnava caselle vuote senza dire niente:
   * un agosto dell'anno prossimo sembrava tutto libero a prezzo zero. Adesso
   * si ricarica quando ci si avvicina, e finché i dati non ci sono lo si dice.
   */
  const [finestra, setFinestra] = useState<{ da: IsoDate; a: IsoDate } | null>(
    null,
  );
  const [oltreLaFinestra, setOltreLaFinestra] = useState(false);
  const [calendario, setCalendario] = useState(false);
  // Cambiare il rapporto o un costo ricorrente cambia i numeri del P&L: la
  // scheda si rimonta invece di restare a mostrare il conto di prima.
  const [versione, setVersione] = useState(0);
  const [schedaAttiva, setScheda] = useState<Scheda>("calendario");

  useEffect(() => {
    if (!listing) return;
    setDays(null);
    setRange(null);
    setCalendario(false);
    // Cambiando casa si riparte dal calendario: è la domanda che si fa per
    // prima, e ritrovarsi nei conti di un'altra casa disorienta.
    setScheda("calendario");
    setFloor(
      listing.floorPriceCents ? String(listing.floorPriceCents / 100) : "",
    );
    setCin(listing.cin ?? "");

    const from = addDays(todayInRome(), -31);
    const to = addDays(from, 240);
    setFinestra({ da: from, a: to });
    setOltreLaFinestra(false);
    void api
      .calendar(listing.id, from, to)
      .then(({ calendar }) => setDays(calendar.days))
      .catch(() => setDays([]));
  }, [listing]);

  if (!listing) {
    return (
      <div className="grid flex-1 place-items-center">
        <EmptyState
          icon={<Building2 size={24} />}
          title="Scegli un immobile"
          hint="Il calendario si apre qui."
        />
      </div>
    );
  }

  /**
   * Si sta guardando un mese fuori dalla finestra caricata: si allarga.
   *
   * Il calendario chiede due mesi per volta, quindi si guarda un mese più in
   * là del primo visibile.
   */
  const guardaMese = (mese: string) => {
    if (!listing || !finestra) return;
    const primoGiorno = `${mese}-01` as IsoDate;
    const oltre = addDays(primoGiorno, 62);
    if (primoGiorno >= finestra.da && oltre <= finestra.a) {
      setOltreLaFinestra(false);
      return;
    }

    setOltreLaFinestra(true);
    const da =
      primoGiorno < finestra.da ? addDays(primoGiorno, -31) : finestra.da;
    const a = oltre > finestra.a ? addDays(oltre, 31) : finestra.a;
    void api
      .calendar(listing.id, da, a)
      .then(({ calendar }) => {
        setDays(calendar.days);
        setFinestra({ da, a });
        setOltreLaFinestra(false);
      })
      .catch(() => setOltreLaFinestra(true));
  };

  const saveFloor = async () => {
    setSaving(true);
    try {
      const value =
        floor.trim() === ""
          ? null
          : Math.round(Number(floor.replace(",", ".")) * 100);
      if (value !== null && (!Number.isFinite(value) || value < 0)) {
        store.notify("Il prezzo minimo non è un numero valido.");
        return;
      }
      await api.setFloorPrice(listing.id, value);
      // Lo stato locale segue il salvataggio, come già faceva il CIN: senza,
      // la riga sotto continuava a dire "minimo 80 €" dopo che lo avevi messo
      // a 120, e restava sbagliata finché non cambiavi casa e tornavi.
      listing.floorPriceCents = value;
      setFloor(value === null ? "" : String(value / 100));
      store.notify(
        value === null
          ? "Prezzo minimo rimosso."
          : `Prezzo minimo a ${formatEur(value)}. Non ci scendo sotto, nemmeno se me lo chiedi.`,
      );
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setSaving(false);
    }
  };

  const saveCin = async () => {
    setSavingCin(true);
    try {
      const valore = cin.trim().toUpperCase();
      await api.setCin(listing.id, valore === "" ? null : valore);
      listing.cin = valore === "" ? null : valore;
      store.notify(
        valore === ""
          ? "CIN rimosso."
          : `CIN salvato: ${valore}. Se me lo chiedi in chat, te lo dico.`,
      );
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setSavingCin(false);
    }
  };

  const ask = (verbo: string) => {
    if (!range) return;
    setCalendario(false);
    onAskOtto(
      `${verbo} su "${listing.nickname}" ${formatRange(range.from, range.to)}`,
    );
  };

  return (
    <>
      {/* ── Testata ───────────────────────────────────────────────────── */}
      <header className="flex items-center gap-3 border-b border-line px-4 py-4 sm:px-6">
        <IconButton
          label="Indietro"
          tone="sunken"
          onClick={onBack}
          className="md:hidden"
        >
          <ArrowLeft size={16} />
        </IconButton>
        <Thumb src={listing.photoUrl} alt={listing.nickname} size={44} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[16px] font-extrabold leading-tight text-ink">
            {listing.nickname}
          </p>
          <p className="truncate text-[13px] text-ink-soft">
            {listing.city}
            {listing.accommodates
              ? ` · ${listing.accommodates} posti letto`
              : ""}
          </p>
        </div>
        {listing.stats?.avgPriceCents ? (
          <span className="hidden shrink-0 rounded-pill bg-surface-muted px-3 py-1.5 text-[13px] font-bold text-ink sm:inline">
            {formatEur(listing.stats.avgPriceCents)}/notte
          </span>
        ) : null}
        <div className="hidden shrink-0 gap-1 sm:flex">
          {listing.channels.map((channel) => (
            <Badge key={channel}>{channel}</Badge>
          ))}
        </div>
      </header>

      {/* ── I titoli: si sceglie cosa guardare, non si guarda tutto ───── */}
      <nav className="flex gap-1.5 border-b border-line px-4 pb-4 pt-4 sm:px-7">
        {SCHEDE.map((scheda) => (
          <button
            key={scheda.id}
            type="button"
            onClick={() => setScheda(scheda.id)}
            className={cx(
              "flex items-center gap-2 rounded-pill px-4 py-2 text-[13.5px] font-bold",
              "transition-all duration-calm ease-soft",
              scheda.id === schedaAttiva
                ? "bg-ink text-ink-inverse shadow-soft"
                : "text-ink-soft hover:bg-surface-muted hover:text-ink",
            )}
          >
            <scheda.Icon size={15} />
            {scheda.label}
          </button>
        ))}
      </nav>

      {/* ── Corpo: una scheda per volta ────────────────────────────────── */}
      <div className="scroll-soft min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-7">
        <div
          key={schedaAttiva}
          className="animate-rise mx-auto w-full max-w-[1240px]"
        >
          {/* ── Calendario ────────────────────────────────────────────── */}
          {schedaAttiva === "calendario" ? (
            <CalendarioTab
              days={days}
              listing={listing}
              onOpen={() => setCalendario(true)}
            />
          ) : null}

          {/* ── Conti ─────────────────────────────────────────────────── */}
          {schedaAttiva === "conti" ? (
            <ContiTab
              key={versione}
              listingId={listing.id}
              onCambiato={() => setVersione((n) => n + 1)}
            />
          ) : null}

          {/* ── Annuncio ──────────────────────────────────────────────── */}
          {schedaAttiva === "annuncio" ? (
            <div className="flex flex-col gap-7">
              <AnnuncioCard listing={listing} onAskOtto={onAskOtto} />

              <Sezione
                icon={<Sparkles size={15} />}
                titolo="Regole e scorciatoie"
                sottotitolo="Il prezzo sotto cui non scendo, il codice di legge, e le domande da cui partire."
              >
                {/* Due schede gemelle: stessa larghezza e stessa altezza.
                  Con altezze diverse sembrano due cose di peso diverso. */}
                <div className="grid items-stretch gap-5 lg:grid-cols-3">
                  <Card className="flex h-full flex-col p-5">
                    <h3 className="mb-1 flex items-center gap-1.5 text-[13px] font-extrabold text-ink">
                      <Euro size={14} className="text-accent" />
                      Prezzo minimo
                    </h3>
                    <p className="mb-2.5 text-[12.5px] leading-relaxed text-ink-soft">
                      Sotto questa cifra non scendo mai, nemmeno se me lo chiedi
                      in chat.
                    </p>
                    <div className="mt-auto flex gap-2">
                      <div className="flex flex-1 items-center gap-1.5 rounded-field bg-surface-muted px-3">
                        <span className="text-sm text-ink-faint">€</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={floor}
                          onChange={(event) => setFloor(event.target.value)}
                          placeholder="nessuno"
                          aria-label="Prezzo minimo"
                          className="w-full bg-transparent py-2.5 text-[15px] tabular-nums text-ink outline-none placeholder:text-ink-faint"
                        />
                      </div>
                      <Pill
                        variant="primary"
                        size="md"
                        onClick={saveFloor}
                        loading={saving}
                        icon={<Check size={15} />}
                        aria-label="Salva il prezzo minimo"
                      >
                        Salva
                      </Pill>
                    </div>
                  </Card>

                  <Card className="flex h-full flex-col p-5">
                    <h3 className="mb-1 flex items-center gap-1.5 text-[13px] font-extrabold text-ink">
                      <FileText size={14} className="text-accent" />
                      CIN
                    </h3>
                    <p className="mb-2.5 text-[12.5px] leading-relaxed text-ink-soft">
                      Il Codice Identificativo Nazionale. Guesty non lo ha: lo
                      scrivi qui una volta e Otto lo sa.
                    </p>
                    <div className="mt-auto flex gap-2">
                      <div className="flex flex-1 items-center rounded-field bg-surface-muted px-3">
                        <input
                          type="text"
                          value={cin}
                          onChange={(event) => setCin(event.target.value)}
                          placeholder="IT…"
                          aria-label="CIN"
                          spellCheck={false}
                          className="w-full bg-transparent py-2.5 font-mono text-[14px] uppercase tracking-wide text-ink outline-none placeholder:text-ink-faint"
                        />
                      </div>
                      <Pill
                        variant="primary"
                        size="md"
                        onClick={saveCin}
                        loading={savingCin}
                        icon={<Check size={15} />}
                        aria-label="Salva il CIN"
                      >
                        Salva
                      </Pill>
                    </div>
                  </Card>

                  <Card className="flex h-full flex-col p-5">
                    <h3 className="mb-1 text-[13px] font-extrabold text-ink">
                      Chiedi a Otto
                    </h3>
                    <p className="mb-2.5 text-[12.5px] leading-relaxed text-ink-soft">
                      Se non sai da dove partire, parti da una domanda.
                    </p>
                    <div className="mt-auto flex flex-wrap gap-2">
                      <Pill
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          onAskOtto(`Come sta andando "${listing.nickname}"?`)
                        }
                      >
                        Come sta andando
                      </Pill>
                      <Pill
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          onAskOtto(
                            `Cosa manca nell'annuncio di "${listing.nickname}"?`,
                          )
                        }
                      >
                        Cosa manca
                      </Pill>
                      <Pill
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          onAskOtto(
                            `Riscrivi tutto l'annuncio di "${listing.nickname}" tenendo il tono che ha adesso`,
                          )
                        }
                      >
                        Riscrivi tutto
                      </Pill>
                    </div>
                  </Card>
                </div>
              </Sezione>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Il calendario: a piena pagina, si chiude con la X ──────────── */}
      <Modal
        open={calendario}
        onClose={() => setCalendario(false)}
        onBack={() => setCalendario(false)}
        backLabel="Torna all'immobile"
        title={listing.nickname}
        subtitle={
          // Anche qui: il calendario che si guarda per decidere un prezzo deve
          // dire da quando è fermo.
          daQuandoLetto(listing.cacheUpdatedAt)
            ? `Tocca due giorni per scegliere un intervallo · letto dal gestionale ${daQuandoLetto(listing.cacheUpdatedAt)}`
            : "Tocca due giorni per scegliere un intervallo"
        }
        media={
          <Thumb src={listing.photoUrl} alt={listing.nickname} size={40} />
        }
        footer={
          <div className="mx-auto flex w-full max-w-[1240px] flex-wrap items-center gap-2">
            {range ? (
              <>
                <p className="mr-1 text-[14px] text-ink-soft">
                  <b className="text-ink">
                    {formatRange(range.from, range.to)}
                  </b>{" "}
                  — cosa faccio?
                </p>
                <Pill
                  variant="primary"
                  size="sm"
                  icon={<Wand2 size={14} />}
                  onClick={() => ask("Alza i prezzi del 10%")}
                >
                  Alza del 10%
                </Pill>
                <Pill
                  variant="secondary"
                  size="sm"
                  onClick={() => ask("Abbassa i prezzi del 10%")}
                >
                  Abbassa del 10%
                </Pill>
                <Pill
                  variant="secondary"
                  size="sm"
                  onClick={() => ask("Blocca il calendario")}
                >
                  Blocca
                </Pill>
                <Pill
                  variant="secondary"
                  size="sm"
                  onClick={() => ask("Metti minimo 3 notti")}
                >
                  Minimo 3 notti
                </Pill>
                <Pill
                  variant="ghost"
                  size="sm"
                  icon={<MessageSquare size={14} />}
                  onClick={() => ask("Cosa mi consigli")}
                >
                  Chiedi a Otto
                </Pill>
                <Pill
                  variant="ghost"
                  size="sm"
                  onClick={() => setRange(null)}
                  className="ml-auto"
                >
                  Deseleziona
                </Pill>
              </>
            ) : (
              <p className="text-[14px] text-ink-soft">
                Scegli un giorno di inizio e uno di fine: qui compaiono le cose
                che posso fare su quell'intervallo.
              </p>
            )}
          </div>
        }
      >
        {days ? (
          <div className="mx-auto w-full max-w-[1240px]">
            <MonthCalendar
              days={days}
              selected={range}
              onSelectRange={(from, to) => setRange({ from, to })}
              onMeseVisibile={guardaMese}
            />
            {oltreLaFinestra ? (
              <p className="mt-3 rounded-card bg-warn-soft px-3 py-2.5 text-[13px] leading-relaxed text-warn">
                Sto leggendo questo periodo dal gestionale. Finché non arriva,
                le caselle vuote vogliono dire «non lo so», non «libero».
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-[360px]" />
          </div>
        )}
      </Modal>
    </>
  );
}

// ── La scheda Calendario: quattro numeri, il mese in corso, chi arriva ──

function CalendarioTab({
  days,
  listing,
  onOpen,
}: {
  days: CalendarDayView[] | null;
  listing: ListingView;
  onOpen: () => void;
}) {
  const oggi = todayInRome();
  const mese = useMemo(() => meseInCorso(oggi), [oggi]);

  const prossime = useMemo(() => {
    if (!days) return null;
    // **Il mese in corso, non i prossimi trenta giorni.** Finché settembre non
    // è finito l'host guarda settembre, e accanto ha il pannello del
    // gestionale che mostra il mese di calendario: due periodi diversi per la
    // stessa casa si leggono come due conti sbagliati.
    const finestra = days.filter(
      (day) => day.date >= mese.da && day.date <= mese.a,
    );
    const vendute = finestra.filter((day) => day.booked);
    // Le notti bloccate a mano non sono libere e non sono vendute: stanno
    // fuori dal conto. Contarle fra le libere voleva dire mostrare «12 notti
    // libere» a chi si è tenuto due settimane per sé, e un'occupazione al 50%
    // su una casa che ha venduto tutto quello che aveva messo in vendita.
    const bloccate = finestra.filter(notteBloccata);
    const inVendita = finestra.length - bloccate.length;
    // Le notti libere **già passate** non si vendono più: contarle in un
    // numero che finisce con «valgono 2.697 €» sarebbe una promessa su
    // giorni che non esistono. L'occupazione guarda tutto il mese, questo
    // guarda da oggi in avanti.
    const daVendere = finestra.filter(
      (day) => day.date >= oggi && !day.booked && !notteBloccata(day),
    );
    return {
      finestra,
      totali: inVendita,
      vendute: vendute.length,
      libere: inVendita - vendute.length,
      daVendere: daVendere.length,
      bloccate: bloccate.length,
      // Quante notti del mese abbiamo davvero in cache: se il collegamento è
      // recente, l'inizio del mese può mancare, e un denominatore più corto
      // va detto, non nascosto.
      nottiLette: finestra.length,
      nottiDelMese: nightsInRange(mese.da, mese.a),
    };
  }, [days, oggi, mese]);

  // L'incasso del mese è quello dei Conti, non la somma dei prezzi di
  // calendario delle notti occupate. Quella somma (verificata su Buniva il
  // 2/10/2026: 2.260 € contro i 2.129 € dei Conti e di Guesty) sbagliava due
  // volte: il prezzo di calendario non è quello pagato dall'ospite, e la notte
  // del primo del mese di un soggiorno cominciato a settembre è di settembre,
  // perché Guesty conta per check-in. Due numeri per la stessa cosa, sulla
  // stessa casa, e l'host smette di credere a entrambi.
  const [incasso, setIncasso] = useState<{
    cents: number;
    nessunCheckIn: boolean;
  } | null>(null);
  const [incassoFallito, setIncassoFallito] = useState(false);
  useEffect(() => {
    let vivo = true;
    setIncasso(null);
    setIncassoFallito(false);
    api
      .profitto(listing.id, mese.da.slice(0, 7))
      .then(({ profitto }) => {
        if (!vivo) return;
        setIncasso({
          cents: profitto.ricaviLordiCents,
          nessunCheckIn: profitto.nessunCheckInNelMese,
        });
      })
      .catch(() => vivo && setIncassoFallito(true));
    return () => {
      vivo = false;
    };
  }, [listing.id, mese.da]);

  const occupancy = listing.stats?.occupancyPercent ?? null;
  const quota =
    prossime && prossime.totali > 0 ? prossime.vendute / prossime.totali : 0;

  // Da quando è ferma questa lettura. Un calendario di ieri e uno di adesso
  // si leggono identici: se non lo si scrive, l'host non ha modo di saperlo.
  const letto = daQuandoLetto(listing.cacheUpdatedAt);
  const nome = nomeMese(mese.da);
  const titolo = nome.charAt(0).toUpperCase() + nome.slice(1);
  // Una parte del mese può mancare dalla cache su un collegamento recente:
  // il denominatore più corto si dice, non si lascia indovinare.
  const parziale =
    prossime != null && prossime.nottiLette < prossime.nottiDelMese;

  return (
    <div className="flex flex-col gap-7">
      <Sezione
        icon={<CalendarDays size={15} />}
        titolo={titolo}
        sottotitolo={
          letto
            ? `Com'è pieno il mese in corso, e a che prezzo. Letto dal gestionale ${letto}.`
            : "Com'è pieno il mese in corso, e a che prezzo. Non l'ho ancora letto dal gestionale."
        }
        azione={
          <Pill
            variant="primary"
            size="sm"
            icon={<CalendarDays size={14} />}
            onClick={onOpen}
          >
            Apri il calendario
          </Pill>
        }
      >
        <GrigliaKpi>
          <Kpi
            icon={<CalendarCheck size={17} />}
            etichetta="Occupazione"
            valore={prossime ? `${Math.round(quota * 100)}%` : ""}
            barra={
              prossime
                ? {
                    quota,
                    estremi: [
                      `${prossime.vendute} vendute`,
                      `${prossime.totali} notti`,
                    ],
                  }
                : null
            }
            dettaglio={
              // Tutto bloccato non è «non so»: è una risposta, e va detta.
              prossime && prossime.totali === 0
                ? `niente in vendita a ${nome}`
                : occupancy == null && !prossime
                  ? "nessun dato di calendario"
                  : parziale && prossime
                    ? `a ${nome} · di ${prossime.nottiDelMese} notti ne ho lette ${prossime.nottiLette}`
                    : prossime && prossime.bloccate > 0
                      ? `a ${nome} · ${prossime.bloccate} ${prossime.bloccate === 1 ? "notte bloccata" : "notti bloccate"}, fuori dal conto`
                      : `a ${nome}, tutto il mese`
            }
            tone={quota >= 0.7 ? "ok" : quota >= 0.35 ? "plain" : "warn"}
            caricamento={!prossime}
          />
          <Kpi
            icon={<Euro size={17} />}
            etichetta="Incasso del mese"
            valore={
              incasso ? formatEur(incasso.cents) : incassoFallito ? "—" : ""
            }
            dettaglio={
              incasso
                ? incasso.nessunCheckIn
                  ? `nessun check-in a ${nome}`
                  : `prenotazioni con check-in a ${nome}, come nei Conti`
                : incassoFallito
                  ? "non sono riuscito a leggerlo"
                  : ""
            }
            caricamento={!incasso && !incassoFallito}
          />
          <Kpi
            icon={<CalendarDays size={17} />}
            etichetta="Notti da vendere"
            valore={prossime ? String(prossime.daVendere) : ""}
            dettaglio={
              prossime
                ? prossime.daVendere === 0
                  ? prossime.totali === 0
                    ? "niente in vendita: è tutto bloccato"
                    : "da oggi a fine mese è tutto venduto"
                  : `da oggi a fine ${nome} · valgono ${formatEur(prossime.daVendere * (listing.stats?.avgPriceCents ?? 0))} al prezzo medio`
                : ""
            }
            tone={
              prossime && prossime.daVendere > 0 && quota < 0.35
                ? "warn"
                : "plain"
            }
            caricamento={!prossime}
          />
          <Kpi
            icon={<Wallet size={17} />}
            etichetta="Prezzo medio"
            valore={
              listing.stats?.avgPriceCents
                ? formatEur(listing.stats.avgPriceCents)
                : "—"
            }
            dettaglio={
              listing.floorPriceCents
                ? `a notte · minimo ${formatEur(listing.floorPriceCents)}`
                : "a notte"
            }
            caricamento={!prossime}
          />
        </GrigliaKpi>
      </Sezione>

      {prossime ? (
        <Card className="p-5">
          <IntestazioneRiquadro
            icon={<CalendarDays size={15} />}
            titolo="Notte per notte"
            nota={`tutto ${nome}`}
            azione={
              <span className="flex items-center gap-3 text-[12px] text-ink-soft">
                <span className="flex items-center gap-1.5">
                  <span className="size-2.5 rounded-[3px] bg-ok" /> venduta
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-2.5 rounded-[3px] bg-surface-sunken ring-1 ring-line" />{" "}
                  libera
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-2.5 rounded-[3px] bg-line-strong" />{" "}
                  bloccata
                </span>
              </span>
            }
          />
          <StrisciaNotti
            giorni={prossime.finestra}
            oggi={oggi}
            onClick={onOpen}
          />
        </Card>
      ) : (
        <Skeleton className="h-28" />
      )}

      {days ? <ProssimiArrivi days={days} /> : <Skeleton className="h-64" />}
    </div>
  );
}

/**
 * Trenta caselle in fila: verde se venduta, vuota se libera. Si legge in un
 * secondo — «la seconda metà del mese è vuota» — e un tap apre il calendario.
 */
function StrisciaNotti({
  giorni,
  oggi,
  onClick,
}: {
  giorni: CalendarDayView[];
  oggi: IsoDate;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="grid w-full grid-cols-10 gap-1.5 sm:grid-cols-[repeat(15,minmax(0,1fr))] lg:grid-cols-[repeat(30,minmax(0,1fr))]"
      aria-label="Apri il calendario"
    >
      {giorni.map((g) => {
        const bloccata = notteBloccata(g);
        return (
          <span
            key={g.date}
            title={`${g.date}${g.priceCents ? ` · ${formatEur(g.priceCents)}` : ""}${g.guestName ? ` · ${g.guestName}` : ""}`}
            className="flex flex-col items-center gap-1"
          >
            <span
              className={cx(
                "h-9 w-full rounded-[6px] transition-transform duration-quick hover:scale-105",
                g.booked
                  ? "bg-ok"
                  : bloccata
                    ? "bg-line-strong"
                    : "bg-surface-sunken ring-1 ring-inset ring-line",
                g.date === oggi && "ring-2 ring-inset ring-ink",
              )}
            />
            <span
              className={cx(
                "text-[10px] tabular-nums",
                g.date === oggi ? "font-bold text-ink" : "text-ink-faint",
              )}
            >
              {Number(g.date.slice(8, 10))}
            </span>
          </span>
        );
      })}
    </button>
  );
}

// ── Prossimi arrivi ─────────────────────────────────────────────────────────

interface Soggiorno {
  id: string;
  from: IsoDate;
  to: IsoDate;
  guestName: string | null;
  nights: number;
}

/**
 * Le notti prenotate arrivano una per una: qui tornano a essere soggiorni.
 *
 * Un property manager non pensa "il 12, il 13 e il 14 sono occupati": pensa
 * "i Rossi stanno tre notti". Le notti consecutive con la stessa prenotazione
 * si ricuciono in una riga sola.
 */
export function raggruppaSoggiorni(days: CalendarDayView[]): Soggiorno[] {
  const ordinati = [...days]
    .filter((day) => day.booked)
    .sort((a, b) => a.date.localeCompare(b.date));
  const soggiorni: Soggiorno[] = [];

  for (const day of ordinati) {
    const corrente = soggiorni.at(-1);
    const stessaPrenotazione =
      corrente != null &&
      corrente.id ===
        (day.reservationId ?? `senza-id:${day.guestName ?? ""}`) &&
      addDays(corrente.to, 1) === day.date;

    if (stessaPrenotazione) {
      corrente.to = day.date;
      corrente.nights += 1;
      continue;
    }

    soggiorni.push({
      id: day.reservationId ?? `senza-id:${day.guestName ?? ""}`,
      from: day.date,
      to: day.date,
      guestName: day.guestName,
      nights: 1,
    });
  }

  return soggiorni;
}

function ProssimiArrivi({ days }: { days: CalendarDayView[] }) {
  const oggi = todayInRome();
  const soggiorni = useMemo(
    () =>
      raggruppaSoggiorni(days)
        .filter((s) => s.to >= oggi)
        .slice(0, 6),
    [days, oggi],
  );

  return (
    <Card className="p-5">
      <IntestazioneRiquadro
        icon={<CalendarCheck size={15} />}
        titolo="Prossimi soggiorni"
        nota={
          soggiorni.length === 0
            ? "nessun arrivo in programma"
            : `${soggiorni.length} in arrivo`
        }
      />
      <div className="grid gap-2 lg:grid-cols-2">
        {soggiorni.map((soggiorno) => (
          <div
            key={`${soggiorno.id}-${soggiorno.from}`}
            className="flex items-center gap-3 rounded-field bg-surface-muted px-3.5 py-2.5"
          >
            <Avatar name={soggiorno.guestName ?? "Ospite"} size={34} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13.5px] font-bold text-ink">
                {soggiorno.guestName ?? "Ospite"}
              </p>
              <p className="truncate text-[12.5px] text-ink-soft">
                {formatRange(soggiorno.from, soggiorno.to)}
              </p>
            </div>
            <span className="shrink-0 text-[12px] font-semibold tabular-nums text-ink-faint">
              {soggiorno.nights} nott{soggiorno.nights === 1 ? "e" : "i"}
            </span>
            {soggiorno.from <= oggi ? <Badge tone="ok">in casa</Badge> : null}
          </div>
        ))}
      </div>
    </Card>
  );
}
